import React from "react";
import PropTypes from "prop-types";
import { Card, CardTitle, CardText, Button, MenuButton } from "react-md";
import moment from "moment-timezone";
import MGrid, { ACTION_CONFIGURE, ACTION_DOWNLOAD } from "components/Shared/MGrid.tsx";
import { transformEmployeeList } from "components/Shared/TimeLogs/Definitions";
import PayPeriodDropdown from "components/User/PayPeriodDropdown.tsx";
import { MSpinner } from "components/Shared";
import GroupedTimeLogTable from "./GroupedTimeLogTable.tsx";
import { BUSOP_AGENT, QUALITY_COMPLIANCE_AUDITOR, VALUATION_QUALITY_ASSOCIATE } from "typeConstants/employeeRoles";

// QA Payroll uses a different grid
const qaRoles = [BUSOP_AGENT, QUALITY_COMPLIANCE_AUDITOR, VALUATION_QUALITY_ASSOCIATE];

class EmployeeListCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPayPeriodIndex: 0,
      currentInternetUserId: 0,
      currentRowIndex: -1,
      managerIndex: 0,
      managers: [],
      dataLastChanged: new Date(),
      selectedQadInternetUserId: null,
      traineeFilterValue: null,
      statusFilterValue: null
    };
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowClickUserId = this.handleRowClickUserId.bind(this);
    this.handleApproveClick = this.handleApproveClick.bind(this);
    this.handleDisableApprove = this.handleDisableApprove.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.setManagers = this.setManagers.bind(this);
    this.getUserFromNestedArrays = this.getUserFromNestedArrays.bind(this);

    this.isQaPayroll = qaRoles.includes(this.props.employeeRole);
    this.traineeFilterMetadata = [
      { primaryText: "Clear Selection", filterValue: null },
      { primaryText: "Is Trainee", filterValue: 1 },
      { primaryText: "Is Not Trainee", filterValue: 2 }
    ];
    this.employeeStatusMetaData = [
      { primaryText: "Clear Selection", filterValue: null },
      { primaryText: "Active", filterValue: 1 },
      { primaryText: "Inactive", filterValue: 2 }
    ];
  }

  componentDidMount() {
    // Using 0 for internerUserId will get the payroll data for the current logged in user
    this.props.getData(0, this.props.payPeriods[0].id);
    // QA payroll grid no longer has manager selection
    if (!qaRoles.includes(this.props.employeeRole)) {
      this.props.getManagers();
      if (!this.state.managers.length && !this.props.isFetchingManagers) {
        this.setManagers();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const traineeFilterValueChanged = prevState.traineeFilterValue !== this.state.traineeFilterValue;
    const statusFilterValueChanged = prevState.statusFilterValue !== this.state.statusFilterValue;
    const payPeriodChanged = prevState.selectedPayPeriodIndex !== this.state.selectedPayPeriodIndex;
    const selectedQadChanged = prevState.selectedQadInternetUserId !== this.state.selectedQadInternetUserId;
    const finishedApproving = prevProps.isApproving && !this.props.isApproving;
    const qadFilterRemoved = prevState.selectedQadInternetUserId !== null && this.state.selectedQadInternetUserId === null;

    if (!this.props.isFetchingData && prevProps.isFetchingData) {
      if (this.props.data.length) {
        this.props.setInternetUserId(this.props.data[0]?.internetUserId);
      }
      this.setState({ dataLastChanged: new Date() });
    }
    if (this.state.selectedQadInternetUserId && (selectedQadChanged || traineeFilterValueChanged || statusFilterValueChanged || payPeriodChanged || finishedApproving)) {
      this.props.getData(
        this.state.selectedQadInternetUserId,
        this.props.payPeriods[this.state.selectedPayPeriodIndex].id,
        this.state.traineeFilterValue,
        this.state.statusFilterValue
      );
    }
    else if (finishedApproving || traineeFilterValueChanged || payPeriodChanged || qadFilterRemoved || statusFilterValueChanged) {
      this.props.getData(
        this.state.managers[this.state.managerIndex]?.internetUserId ?? 0,
        this.props.payPeriods[this.state.selectedPayPeriodIndex].id,
        this.state.traineeFilterValue,
        this.state.statusFilterValue
      );
    }
    if (!qaRoles.includes(this.props.employeeRole)) {
      if (!this.props.isFetchingManagers && prevProps.isFetchingManagers) {
        this.setManagers();
      }
    }
  }

  setManagers() {
    // add My Managers and remove the name from managers list if its the current user.
    let managers = this.props.managers?.map((x) => ({
      primaryText: `${x.firstLastName}`,
      internetUserId: x.internetUserId,
      onClick: this.handleClick,
    }));
    managers.unshift({ primaryText: "My Employees", internetUserId: 0, onClick: this.handleClick });
    let currentUserIndex = managers.findIndex((x) => x.internetUserId === this.props.profile.internetUserId);
    if (currentUserIndex !== -1) {
      managers.splice(currentUserIndex, 1);
    }
    this.setState({ managers: managers });
  }

  handleRowClick(rowIndex) {
    this.props.setInternetUserId(this.props.data[rowIndex].internetUserId, { ...this.props.data[rowIndex] });
    this.setState({
      currentRowIndex: rowIndex,
      currentInternetUserId: this.props.data[rowIndex].internetUserId,
    });
  }

  getUserFromNestedArrays(internetUserId) {
    return this.props?.data?.flatMap((arr) => arr.employeeList ?? [])?.find((d) => d.internetUserId === internetUserId);
  }

  // Used with GroupedTimeLogTable so we don't have to worry about passing around multiple indexes as the data structure has changed
  handleRowClickUserId(internetUserId) {
    const user = this.getUserFromNestedArrays(internetUserId);
    if (user) {
      this.props.setInternetUserId(internetUserId, user);
      this.setState({
        currentInternetUserId: internetUserId,
      });
    }
  }

  handleApproveClick() {
    this.props.approve(this.props.payPeriods[this.state.selectedPayPeriodIndex].id, this.state.currentInternetUserId);
  }

  handleDisableApprove() {
    const isQaRole = qaRoles.includes(this.props.employeeRole);
    if (!isQaRole && this.state.currentRowIndex === -1) {
      return true;
    }
    const currentPayPeriod = this.props.payPeriods[this.state.selectedPayPeriodIndex];
    const { closedDate, toDate } = currentPayPeriod;
    if (closedDate) {
      return true;
    }
    if (
      !isQaRole &&
      this.props.data &&
      this.props.data.length > this.state.currentRowIndex &&
      this.props.data[this.state.currentRowIndex].isApproved
    ) {
      return true;
    } else if (isQaRole && this.props.data?.length) {
      const user = this.getUserFromNestedArrays(this.state.currentInternetUserId);
      if (user && user.isApproved) {
        return true;
      }
    }
    if (moment().diff(moment({ year: toDate.year, month: toDate.month - 1, day: toDate.day })) <= 0) {
      return true;
    }
    return false;
  }

  handleClick(e) {
    let managerIndex = this.state.managers.findIndex((x) => x.primaryText === e.target.innerText);
    this.props.getData(
      this.state.managers[managerIndex].internetUserId,
      this.props.payPeriods[this.state.selectedPayPeriodIndex].id
    );
    this.setState({ managerIndex: managerIndex });
    if (this.props.onManagerChangeCallback) this.props.onManagerChangeCallback();
  }

  render() {
    if (this.props.isFetchingManagers) {
      return <MSpinner />;
    }

    const payPeriodLabel = this.props.payPeriods?.[this.state.selectedPayPeriodIndex]?.fromToDisplay ?? "";
    const managerLabel = this.state.managers?.[this.state?.managerIndex]?.primaryText ?? "";
    const downloadFileName =
      this.props.getDownloadFileName?.(managerLabel, payPeriodLabel) ??
      `QA Payroll for ${managerLabel} ${payPeriodLabel}`;
    const actions = [ACTION_CONFIGURE];
    if (this.props.canDownload) actions.push(ACTION_DOWNLOAD);

    return (
      <Card className="card-unnested employee-list-grid" style={{ height: "100%" }}>
        <CardTitle title="Employee List" />
        <CardText>
          <div style={{ display: "flex", height: "100%", flexDirection: "column" }}>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <PayPeriodDropdown
                payPeriods={this.props.payPeriods}
                selectedPayPeriodIndex={this.state.selectedPayPeriodIndex}
                payPeriodChangeCallback={(index) => {
                  this.setState({ selectedPayPeriodIndex: index });
                  this.props.payPeriodChangeCallback(index);
                }}
              />
              {this.props.qadList && this.props.qadList.length > 0 && (
                <div className="qad-dropdown">
                  <MenuButton
                    id="menu-button-3-toggle"
                    position={MenuButton.Positions.BELOW}
                    primary
                    raised
                    menuItems={
                      [
                        { primaryText: "Clear Filter", onClick: () => this.setState({ selectedQadInternetUserId: null }) }
                      ].concat(this.props.qadList.map((qad) => ({ primaryText: qad.firstLastName, onClick: () => this.setState({ selectedQadInternetUserId: qad.internetUserId }) })))}
                    iconChildren="arrow_drop_down"
                  >
                    {this.state.selectedQadInternetUserId ? this.props.qadList.find(qad => qad.internetUserId === this.state.selectedQadInternetUserId)?.firstLastName : "Filter by QAD"}
                  </MenuButton>
                </div>
              )}
              {this.isQaPayroll && (
                <div className="trainee-dropdown">
                  <MenuButton
                    id="menu-button-3-toggle"
                    position={MenuButton.Positions.BELOW}
                    primary
                    raised
                    menuItems={this.traineeFilterMetadata.map(f => ({ primaryText: f.primaryText, onClick: () => this.setState({ traineeFilterValue: f.filterValue }) }))}
                    iconChildren="arrow_drop_down"
                  >
                    {this.traineeFilterMetadata.find(f => f.filterValue === this.state.traineeFilterValue && f.filterValue !== null)?.primaryText ?? "Filter by Trainee"}
                  </MenuButton>
                </div>
              )}
              {this.isQaPayroll && (
                <div className="active-employee-dropdown">
                  <MenuButton
                    id="menu-button-3-toggle"
                    position={MenuButton.Positions.BELOW}
                    primary
                    raised
                    menuItems={this.employeeStatusMetaData.map(f => ({ primaryText: f.primaryText, onClick: () => this.setState({ statusFilterValue: f.filterValue }) }))}
                    iconChildren="arrow_drop_down"
                  >
                    {this.employeeStatusMetaData.find(f => f.filterValue === this.state.statusFilterValue && f.filterValue !== null)?.primaryText ?? "Filter by Active Status"}
                  </MenuButton>
                </div>
              )}
              <Button
                className="approve-button"
                raised
                primary
                onClick={this.handleApproveClick}
                disabled={this.handleDisableApprove()}
                style={{ marginLeft: 5, marginRight: 10, padding: 3, overflow: "hidden" }}
              >
                Reviewed
              </Button>
              {this.props.extraFilters}
            </div>
            {!qaRoles.includes(this.props.employeeRole) && !this.props.extraFilters && (
              <div className="my-time-log-filter" style={{ display: "block", marginTop: 5 }}>
                <MenuButton
                  id="menu-button-3-toggle"
                  position={MenuButton.Positions.BELOW}
                  primary
                  raised
                  menuItems={this.state.managers.map((x) => ({ primaryText: x.primaryText, onClick: x.onClick }))}
                  iconChildren="arrow_drop_down"
                >
                  {this.state.managers[this.state.managerIndex]
                    ? this.state.managers[this.state.managerIndex].primaryText
                    : ""}
                </MenuButton>
              </div>
            )}
            <hr style={{ width: "100%" }} />
            {qaRoles.includes(this.props.employeeRole) ? (
              <GroupedTimeLogTable
                columnDefinitions={this.props.getColumnDefinitions(this.props.data, this.props.totalsData)}
                data={this.props.data}
                onRowClick={this.handleRowClickUserId}
                downloadFileName={downloadFileName}
                fetchingData={this.props.isFetchingData}
              />
            ) : (
              <MGrid
                columnDefinitions={this.props.getColumnDefinitions(this.props.data, this.props.totalsData)}
                data={transformEmployeeList(this.props.data, this.props.dataFilter)}
                dataLastChanged={this.state.dataLastChanged}
                isFetchingData={this.props.isFetchingData}
                displayNumOfItemsFound={false}
                onRowClick={this.handleRowClick}
                downloadFileName={downloadFileName}
                actions={actions}
              />
            )}
          </div>
        </CardText>
      </Card>
    );
  }
}
EmployeeListCard.propTypes = {
  data: PropTypes.array,
  dataFilter: PropTypes.func,
  isFetchingData: PropTypes.bool.isRequired,
  getData: PropTypes.func.isRequired,
  setInternetUserId: PropTypes.func.isRequired,
  payPeriods: PropTypes.array.isRequired,
  payPeriodChangeCallback: PropTypes.func.isRequired,
  approve: PropTypes.func.isRequired,
  isApproving: PropTypes.bool.isRequired,
  isFetchingManagers: PropTypes.bool.isRequired,
  managers: PropTypes.array,
  getManagers: PropTypes.func.isRequired,
  profile: PropTypes.object,
  getColumnDefinitions: PropTypes.func,
  onManagerChangeCallback: PropTypes.func,
  extraFilters: PropTypes.array,
  getDownloadFileName: PropTypes.func,
  totalsData: PropTypes.object,
  employeeRole: PropTypes.string,
  canDownload: PropTypes.bool,
  qadList: PropTypes.array,
};
export default EmployeeListCard;
